import React from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const teamMembers = [
  {
    name: '陈志远',
    role: '创始人 & 总编辑',
    avatar: 'https://readdy.ai/api/search-image?query=Asian%20male%20CEO%20portrait%20in%20modern%20office%2C%20professional%20attire%2C%20confident%20expression%2C%20warm%20lighting%2C%20simple%20cream%20background%2C%20corporate%20headshot%20style&width=160&height=160&seq=team-ceo&orientation=squarish',
    bio: '资深媒体人，拥有15年亚洲文化内容创作经验。曾任职于多家国际媒体，致力于搭建亚洲内容交流的桥梁。',
  },
  {
    name: '林清雅',
    role: '主编',
    avatar: 'https://readdy.ai/api/search-image?query=Asian%20female%20editor%20portrait%2C%20elegant%20professional%20look%2C%20warm%20indoor%20lighting%2C%20simple%20cream%20background%2C%20editorial%20headshot%20style&width=160&height=160&seq=team-editor&orientation=squarish',
    bio: '专注于亚洲文化与生活方式领域，对东亚和东南亚文化有深入研究。她的文章以细腻的笔触和深入的洞察见长。',
  },
  {
    name: '王大明',
    role: '资深编辑',
    avatar: 'https://readdy.ai/api/search-image?query=Asian%20male%20journalist%20portrait%2C%20casual%20professional%20style%2C%20friendly%20smile%2C%20warm%20natural%20light%2C%20simple%20cream%20background%2C%20editorial%20headshot&width=160&height=160&seq=team-senior&orientation=squarish',
    bio: '美食与旅行专栏作者，足迹遍布亚洲30多个国家和地区。用文字和镜头记录下每一次亚洲探索之旅。',
  },
];

const milestones = [
  { year: '2018', title: '亚洲专区成立', description: '由一群热爱亚洲文化的媒体人共同创立，最初以博客形式分享亚洲各地见闻。' },
  { year: '2019', title: '内容体系建立', description: '构建了八大内容分类体系，覆盖东亚、东南亚、南亚三大区域，月活用户突破5万。' },
  { year: '2020', title: '疫情影响下的转型', description: '疫情期间加速数字化转型，推出线上直播、虚拟旅行等创新内容形式，用户增长300%。' },
  { year: '2022', title: '团队扩张', description: '编辑团队扩充至30人，在北京、东京、曼谷设立联络处，实现亚洲核心区域的本地化采编。' },
  { year: '2024', title: '平台化升级', description: '从内容博客升级为综合性亚洲内容平台，引入产品评测、服务推荐等新板块，月活突破30万。' },
  { year: '2026', title: '迈向新高度', description: '月活用户突破50万，内容库超过12,800篇精选文章，成为华语世界最具影响力的亚洲内容平台之一。' },
];

const values = [
  { icon: 'ri-shield-check-line', title: '真实可靠', description: '每篇文章由专业编辑团队采编和审核，确保信息真实、来源可靠。我们拒绝标题党和虚假内容。' },
  { icon: 'ri-lightbulb-line', title: '深度洞察', description: '不止于表面的资讯报道，我们致力于挖掘亚洲文化、经济和社会现象背后的深度逻辑。' },
  { icon: 'ri-global-line', title: '多元包容', description: '尊重亚洲各国的文化多样性，以开放、包容的视角呈现亚洲的丰富面貌。' },
  { icon: 'ri-heart-pulse-line', title: '用户至上', description: '始终以读者需求为核心，持续优化内容质量和阅读体验，打造最优质的亚洲内容平台。' },
];

export default function AboutPage() {
  const [contactStatus, setContactStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [contactError, setContactError] = React.useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setContactStatus('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u073l3pcjqs2fcsjtg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && code === 'OK') {
        setContactStatus('success');
        form.reset();
      } else {
        setContactError(serverMsg || '提交失败，请稍后再试');
        setContactStatus('error');
      }
    } catch {
      setContactError('网络错误，请检查网络连接');
      setContactStatus('error');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'Organization',
        name: '亚洲专区',
        url: window.location.origin,
        description: '汇聚亚洲各地精选优质内容，涵盖文化、娱乐、资讯、产品与服务。',
        foundingDate: '2018',
        founder: { '@type': 'Person', name: '陈志远' },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@wuyajuan.cn',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'AboutPage',
        name: '关于亚洲专区',
        description: '了解亚洲专区的品牌故事、团队背景、发展历程与核心价值观。',
        url: `${window.location.origin}/about`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-24 pb-10">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="mt-8 text-center py-12 md:py-16">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
              关于亚洲专区
            </h1>
            <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              亚洲专区创立于2018年，致力于为全球华语读者提供最全面、最权威的亚洲内容服务。从东亚到南亚，从文化到科技，我们用专业的视角呈现亚洲的多元面貌。
            </p>
          </section>

          {/* Timeline */}
          <section className="py-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-10">
                {milestones.map((m, index) => (
                  <div key={m.year} className={`relative flex flex-col md:flex-row gap-4 md:gap-10 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-10 md:pl-0`}>
                      <div className={`hidden md:block ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                        <span className="inline-block px-3 py-1 text-sm font-bold rounded-full bg-primary-100 text-primary-700 mb-2">
                          {m.year}
                        </span>
                        <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{m.description}</p>
                      </div>
                      <div className="md:hidden">
                        <span className="inline-block px-2.5 py-0.5 text-xs font-bold rounded-full bg-primary-100 text-primary-700 mb-1.5">
                          {m.year}
                        </span>
                        <h3 className="font-heading text-base font-semibold text-foreground-800 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-2 md:left-1/2 top-1.5 w-5 h-5 rounded-full bg-primary-500 border-4 border-background-50 md:-translate-x-1/2"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {values.map((v) => (
                <div key={v.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${v.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Team */}
          <section className="py-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {teamMembers.map((member) => (
                <div key={member.name} className="text-center p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
                    loading="lazy"
                  />
                  <h3 className="font-heading text-base font-semibold text-foreground-800 mb-1">{member.name}</h3>
                  <p className="text-sm text-primary-600 font-medium mb-3">{member.role}</p>
                  <p className="text-sm text-foreground-500 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm md:text-base text-center mb-10 max-w-lg mx-auto">
              如有合作意向、内容建议或任何疑问，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 max-w-[900px] mx-auto">
              <div className="lg:col-span-2 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">contact@wuyajuan.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-wechat-line text-accent-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">微信公众号</h4>
                    <p className="text-sm text-foreground-500">亚洲专区</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">办公地址</h4>
                    <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                {contactStatus === 'success' ? (
                  <div className="p-6 rounded-lg bg-primary-100 border border-primary-200 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-check-line text-white text-xl"></i>
                    </div>
                    <p className="text-primary-700 font-medium">感谢您的留言！我们会尽快回复。</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleContactSubmit}
                    className="space-y-4"
                    data-readdy-form="true"
                    id="contact-form"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <input
                      type="text"
                      name="subject"
                      placeholder="主题"
                      required
                      className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容..."
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    ></textarea>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 text-sm font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                    >
                      发送留言
                    </button>
                  </form>
                )}
                {contactStatus === 'error' && (
                  <p className="mt-3 text-sm text-red-600">{contactError}</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}